import { ChangeEvent, useState } from 'react';
import { Checkbox, FormControlLabel, MenuItem, Stack, TextField } from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { customComponentInternalProps } from 'components/FormCreatorComponent/types';
import { CustomTextField } from 'theme/elements/Inputs';
import {
	businessWebsitePrefixes,
	businessWebsiteUrlMaxLength,
	noBusinessWebsiteValue,
} from 'constants/new_accounts.constants';
import { splitBusinessWebsiteUrl } from 'utils/new-accounts.utils';

export default function BusinessWebsiteField(props: customComponentInternalProps) {
	const t = useAppSelector(selectTranslations);
	const { valid, cField, handleChange } = props;
	const { label, required, disabled } = cField.props;
	const value = cField.props.value as string;
	const [selectedPrefix, setSelectedPrefix] = useState(businessWebsitePrefixes[0]);

	const isNoWebsite = value === noBusinessWebsiteValue;
	const storedWebsite = splitBusinessWebsiteUrl(isNoWebsite ? '' : value);
	const prefix = storedWebsite.prefix || selectedPrefix;

	const handlePrefixChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const nextPrefix = event.target.value;
		const nextValue = storedWebsite.text ? `${nextPrefix}${storedWebsite.text}` : '';
		if (nextValue.length > businessWebsiteUrlMaxLength) {
			return;
		}
		setSelectedPrefix(nextPrefix);
		if (storedWebsite.text) {
			handleChange({ target: { value: nextValue } });
		}
	};

	const handleTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const typedWebsite = splitBusinessWebsiteUrl(event.target.value);
		const nextPrefix = typedWebsite.prefix || prefix;
		const nextValue = typedWebsite.text ? `${nextPrefix}${typedWebsite.text}` : '';
		if (nextValue.length > businessWebsiteUrlMaxLength) {
			return;
		}
		setSelectedPrefix(nextPrefix);
		handleChange({ target: { value: nextValue } });
	};

	const handleNoWebsiteChange = (event: ChangeEvent<HTMLInputElement>) => {
		handleChange({
			target: { value: event.target.checked ? noBusinessWebsiteValue : '' },
		});
	};

	return (
		<CustomTextField>
			<Stack direction="row" gap={1} sx={{ width: '100%' }}>
				<TextField
					label={label}
					value={storedWebsite.text}
					onChange={handleTextChange}
					disabled={disabled || isNoWebsite}
					required={required}
					error={!valid}
					variant="filled"
					autoComplete="off"
					inputProps={{ dir: 'ltr' }}
					sx={{ flex: '1 1 auto', minWidth: 0, '&.MuiFormControl-root': { width: 'auto' } }}
				/>
				<TextField
					select
					value={prefix}
					onChange={handlePrefixChange}
					disabled={disabled || isNoWebsite}
					variant="filled"
					SelectProps={{
						SelectDisplayProps: { dir: 'ltr' },
						renderValue: () => (
							<span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
								{prefix}
							</span>
						),
						MenuProps: { PaperProps: { dir: 'ltr' } },
					}}
					sx={{ flex: '0 0 140px', '&.MuiFormControl-root': { width: 140 } }}
				>
					{businessWebsitePrefixes.map((option) => (
						<MenuItem key={option} value={option} dir="ltr">
							<span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
								{option}
							</span>
						</MenuItem>
					))}
				</TextField>
			</Stack>
			<FormControlLabel
				control={<Checkbox checked={isNoWebsite} onChange={handleNoWebsiteChange} disabled={disabled} />}
				label={t('page.accounts.new_account.customer.no_website')}
			/>
		</CustomTextField>
	);
}
